import type { CSSProperties } from 'react'
import { Link } from 'wouter'

/**
 * One visual language for the actuals cut-off (the model date) everywhere a period appears:
 * actual periods are shaded, forecast periods are plain, a period that spans the cut-off is
 * shaded for its closed share, and a solid line marks the switchover to forecast.
 */
export type PeriodStatus = { months: number; closedMonths: number } | null | undefined
export type Phase = 'actual' | 'forecast' | 'mixed'

export function phaseOf(p: PeriodStatus): Phase | null {
  if (!p?.months) return null
  if (p.closedMonths >= p.months) return 'actual'
  if (p.closedMonths <= 0) return 'forecast'
  return 'mixed'
}

/** Class and style for a period column's header or cell; `switchover` marks the first column after the cut-off. */
export function periodCell(cols: readonly { period?: PeriodStatus }[], i: number): { className: string; style?: CSSProperties } {
  const p = cols[i]?.period
  const ph = phaseOf(p)
  if (!ph) return { className: '' }
  const prev = i > 0 ? phaseOf(cols[i - 1]?.period) : null
  // one line where pure forecast begins; a column that spans the cut-off shows it by its partial shading and "A + F" label
  const switchover = ph === 'forecast' && (prev === 'actual' || prev === 'mixed') ? ' switchover' : ''
  if (ph === 'mixed' && p)
    return { className: `ph-mixed${switchover}`, style: { '--closed': `${(p.closedMonths / p.months) * 100}%` } as CSSProperties }
  return { className: `ph-${ph}${switchover}` }
}

/** Runs of consecutive columns in the same phase, for the band above the headers. */
function runs(cols: readonly { period?: PeriodStatus }[]) {
  const out: { phase: Phase | null; span: number; start: number }[] = []
  cols.forEach((c, i) => {
    const ph = phaseOf(c.period)
    const last = out.at(-1)
    if (last && last.phase === ph && ph !== 'mixed') last.span++
    else out.push({ phase: ph, span: 1, start: i })
  })
  return out
}

const LABEL: Record<Phase, string> = { actual: 'Actuals', forecast: 'Forecast', mixed: 'A + F' }

/**
 * The ACTUALS / FORECAST band over a grid's period columns. `lead` cells precede the
 * periods (row headers); columns without a period (totals, variances) get a blank band.
 */
export function PeriodBand({
  cols,
  lead = 1,
  leadClass = '',
}: {
  cols: readonly { period?: PeriodStatus }[]
  lead?: number
  leadClass?: string
}) {
  if (!cols.some(c => phaseOf(c.period) !== null)) return null
  return (
    <tr className="pband">
      {lead > 0 && <th colSpan={lead} className={leadClass} aria-hidden />}
      {runs(cols).map(r => {
        if (!r.phase) return <th key={r.start} colSpan={r.span} aria-hidden />
        const cell = periodCell(cols, r.start)
        const p = cols[r.start]?.period
        return (
          <th
            key={r.start}
            colSpan={r.span}
            className={`pband-${r.phase} ${cell.className}`}
            style={cell.style}
            title={
              r.phase === 'mixed' && p
                ? `${p.closedMonths} actual + ${p.months - p.closedMonths} forecast months`
                : r.phase === 'actual'
                  ? 'Closed periods: actuals'
                  : 'Open periods: forecast'
            }
          >
            {r.phase === 'mixed' && p && r.span === 1 ? `${p.closedMonths}A + ${p.months - p.closedMonths}F` : LABEL[r.phase]}
          </th>
        )
      })}
    </tr>
  )
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const monthName = (m: string | null | undefined) => {
  if (!m) return ''
  const [y, mo] = m.split('-').map(Number)
  return y && mo ? `${MONTHS[mo - 1]} ${y}` : m
}
export function nextMonth(m: string): string {
  const [y, mo] = m.split('-').map(Number)
  if (!y || !mo) return m
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`
}

/** A short legend for a grid or chart footer. */
export function PeriodLegend({ closedThrough }: { closedThrough: string | null | undefined }) {
  if (!closedThrough) return null
  return (
    <span className="period-legend">
      <span className="sw actual" /> Actuals through {monthName(closedThrough)}
      <span className="sw forecast" /> Forecast from {monthName(nextMonth(closedThrough))}
    </span>
  )
}

/** A period's phase as a small chip: Actuals, Forecast, or 8A + 4F. */
export function PeriodChip({ status }: { status: PeriodStatus }) {
  const ph = phaseOf(status)
  if (!ph || !status) return null
  return (
    <span
      className={`period-chip ${ph}`}
      title={
        ph === 'mixed' ? `${status.closedMonths} months of actuals, ${status.months - status.closedMonths} months of forecast` : undefined
      }
    >
      {ph === 'mixed' ? `${status.closedMonths}A + ${status.months - status.closedMonths}F` : ph === 'actual' ? 'Actuals' : 'Forecast'}
    </span>
  )
}

/**
 * The model date: the actuals cut-off every page reads against (Anaplan calls it the current
 * period; versions switch over from actual to forecast there). Links to closing the next month.
 */
export function ModelDate({ closedThrough, horizonEnd }: { closedThrough: string | null; horizonEnd: string }) {
  return (
    <Link
      href="/data"
      className="model-date"
      title={
        closedThrough
          ? `Model date: months through ${monthName(closedThrough)} show actuals and are read-only; ${monthName(nextMonth(closedThrough))} to ${monthName(horizonEnd)} is forecast. Close the next month under Data.`
          : 'No month has closed yet: every period is forecast. Close a month under Data once actuals land.'
      }
    >
      <span className="sw actual" />
      {closedThrough ? (
        <>
          <span className="faint">Actuals through</span> {monthName(closedThrough)}
        </>
      ) : (
        <span className="faint">No actuals closed</span>
      )}
    </Link>
  )
}
